import { Injectable } from '@angular/core';
import { scrubValue } from './error-scrubber';
import { ErrorReportContext } from './error-reporter';

export type TelemetryKind = 'web-vital' | 'route-timing' | 'metric';

export interface TelemetryEvent {
  kind: TelemetryKind;
  name: string;
  value: number;
  unit?: string;
  release: string;
  environment: string;
  /** Anonymized tenant id (never raw subdomain). */
  tenantHash: string | null;
  moduleId: string | null;
  userRole: number | null;
  timestamp: string;
  extra?: Record<string, unknown>;
}

/** Pluggable sink — default is no-op so CI / local stay quiet (AC-53). */
export abstract class TelemetrySink {
  abstract record(event: TelemetryEvent): void;
}

@Injectable()
export class NoopTelemetrySink extends TelemetrySink {
  record(_event: TelemetryEvent): void {
    // intentionally empty
  }
}

/** Console sink for local debugging when explicitly provided. */
@Injectable()
export class ConsoleTelemetrySink extends TelemetrySink {
  record(event: TelemetryEvent): void {
    // eslint-disable-next-line no-console
    console.info('[Telemetry]', event);
  }
}

/** POST scrubbed events to a collector URL (optional). Construct manually with a URL. */
export class HttpTelemetrySink extends TelemetrySink {
  constructor(private readonly url: string) {
    super();
  }

  record(event: TelemetryEvent): void {
    if (!this.url || typeof fetch === 'undefined') {
      return;
    }
    try {
      fetch(this.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scrubValue(event)),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // ignore
    }
  }
}

/** Stable anonymized tenant id for telemetry (not reversible without salt ops-side). */
export function anonymizeTenantId(tenantId: string | null | undefined): string | null {
  if (!tenantId) {
    return null;
  }
  let h = 2166136261;
  for (let i = 0; i < tenantId.length; i++) {
    h ^= tenantId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `t_${(h >>> 0).toString(16)}`;
}

export function buildTelemetryBase(
  ctx: Pick<ErrorReportContext, 'release' | 'environment' | 'tenantId' | 'userRank' | 'moduleId'>,
): Pick<TelemetryEvent, 'release' | 'environment' | 'tenantHash' | 'moduleId' | 'userRole' | 'timestamp'> {
  return {
    release: ctx.release,
    environment: ctx.environment,
    tenantHash: anonymizeTenantId(ctx.tenantId),
    moduleId: ctx.moduleId,
    userRole: ctx.userRank,
    timestamp: new Date().toISOString(),
  };
}
